import { useEffect, useRef } from "react";

const CONTROL_SELECTOR = "input:not([type='hidden']), select, textarea";
const BUTTON_SELECTOR = "button, input[type='button'], input[type='submit'], input[type='reset']";
const DISABLED_ATTR = "data-permission-readonly-disabled";
const MUTATING_BUTTON_PATTERN = /\b(add|save|submit|update|delete|remove|upload|clear|edit|confirm|void|restore|assign|unassign)\b/i;
const VIEW_BUTTON_PATTERN = /\b(back|cancel|close|download|filter|hide|order|refresh|return|select|show|top|view)\b/i;

function getActionText(element) {
    return [
        element.getAttribute("aria-label"),
        element.getAttribute("title"),
        element.value,
        element.textContent
    ]
        .filter(Boolean)
        .join(" ");
}

function isMutatingButton(element) {
    const type = element.getAttribute("type")?.toLowerCase();
    const actionText = getActionText(element);

    if (type === "submit" || type === "reset") {
        return true;
    }

    if (VIEW_BUTTON_PATTERN.test(actionText)) {
        return false;
    }

    return MUTATING_BUTTON_PATTERN.test(actionText);
}

function setPermissionDisabled(element, disabled) {
    if (disabled) {
        if (!element.hasAttribute(DISABLED_ATTR)) {
            element.setAttribute(DISABLED_ATTR, element.disabled ? "already" : "applied");
        }

        element.disabled = true;
        element.setAttribute("aria-disabled", "true");
        return;
    }

    if (!element.hasAttribute(DISABLED_ATTR)) {
        return;
    }

    const state = element.getAttribute(DISABLED_ATTR);
    element.removeAttribute(DISABLED_ATTR);
    element.removeAttribute("aria-disabled");

    if (state === "applied") {
        element.disabled = false;
    }
}

function syncReadOnlyState(root, readOnly) {
    root.querySelectorAll(CONTROL_SELECTOR).forEach((element) => {
        setPermissionDisabled(element, readOnly);
    });

    root.querySelectorAll(BUTTON_SELECTOR).forEach((element) => {
        setPermissionDisabled(element, readOnly && isMutatingButton(element));
    });
}

function closestButton(target) {
    return target instanceof Element ? target.closest(BUTTON_SELECTOR) : null;
}

const PermissionReadOnlyBoundary = ({ readOnly = false, children }) => {
    const containerRef = useRef(null);

    useEffect(() => {
        const root = containerRef.current;

        if (!root) {
            return undefined;
        }

        syncReadOnlyState(root, readOnly);

        if (!readOnly) {
            return () => syncReadOnlyState(root, false);
        }

        const observer = new MutationObserver(() => {
            syncReadOnlyState(root, true);
        });

        observer.observe(root, {
            childList: true,
            subtree: true
        });

        return () => {
            observer.disconnect();
            syncReadOnlyState(root, false);
        };
    }, [readOnly]);

    const handleSubmitCapture = (event) => {
        if (!readOnly) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
    };

    const handleClickCapture = (event) => {
        if (!readOnly) {
            return;
        }

        const button = closestButton(event.target);

        if (!button || !containerRef.current?.contains(button) || !isMutatingButton(button)) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
    };

    return (
        <div
            ref={containerRef}
            data-permission-readonly={readOnly ? "true" : "false"}
            onSubmitCapture={handleSubmitCapture}
            onClickCapture={handleClickCapture}
        >
            {children}
        </div>
    );
};

export default PermissionReadOnlyBoundary;
